import React, { useState, useEffect } from 'react';
import {
  Container,
  ExpandableSection,
  FileUpload,
  SpaceBetween,
  Box,
  ProgressBar,
  Button,
  Alert
} from '@awsui/components-react';
import competitiveResearchWebSocketService from '../../services/CompetitiveResearchWebSocketService';

export function FileUploadPanel({ userId, onFilesUploaded }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadError, setUploadError] = useState(null);
  const [pendingUploads, setPendingUploads] = useState({});

  useEffect(() => {
    const unsubscribe = competitiveResearchWebSocketService.onMessage('upload_url_ready', async (message) => {
      const { doc_id, s3_key, presigned_post, temp_ref } = message;
      
      let file = temp_ref ? pendingUploads[temp_ref] : null;
      
      if (!file) {
        const filename = s3_key.split('/').pop();
        file = Object.values(pendingUploads).find(f => f.name === filename);
      }
      
      if (!file) return;
      
      setPendingUploads(prev => ({ ...prev, [doc_id]: file }));
      
      try {
        const formData = new FormData();
        Object.keys(presigned_post.fields).forEach(key => {
          formData.append(key, presigned_post.fields[key]);
        });
        formData.append('file', file);
        
        const response = await fetch(presigned_post.url, {
          method: 'POST',
          body: formData
        });
        
        if (response.ok || response.status === 204) {
          competitiveResearchWebSocketService.sendMessage({
            action: 'register_upload',
            userId: userId,
            doc_id: doc_id,
            s3_key: s3_key,
            filename: file.name,
            file_type: file.name.split('.').pop()
          });
          
          setUploadProgress(prev => ({ ...prev, [doc_id]: 100 }));
        } else {
          throw new Error(`S3 upload failed with status ${response.status}`);
        }
      } catch (error) {
        setUploadError(`Failed to upload ${file.name}`);
        setUploadProgress(prev => ({ ...prev, [doc_id]: -1 }));
      }
    });
    
    const unsubscribe2 = competitiveResearchWebSocketService.onMessage('upload_complete', (message) => {
      const { doc_id } = message;
      const file = pendingUploads[doc_id];
      
      if (file) {
        setUploadedDocs(prev => [...prev, { doc_id, filename: file.name }]);
      }
    });
    
    return () => {
      unsubscribe();
      unsubscribe2();
    };
  }, [pendingUploads, userId]);
  
  useEffect(() => {
    if (onFilesUploaded) {
      onFilesUploaded(uploadedDocs);
    }
  }, [uploadedDocs, onFilesUploaded]);

  const handleFileChange = ({ detail }) => {
    const files = detail.value;
    
    const validTypes = ['.pdf', '.docx', '.pptx'];
    const invalidFiles = files.filter(f => 
      !validTypes.some(ext => f.name.toLowerCase().endsWith(ext))
    );
    
    if (invalidFiles.length > 0) {
      setUploadError(`Invalid file types: ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }
    
    if (files.length > 10) {
      setUploadError('Maximum 10 files allowed');
      return;
    }
    
    const oversizedFiles = files.filter(f => f.size > 52428800);
    if (oversizedFiles.length > 0) {
      setUploadError(`Files exceed 50MB: ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }
    
    setUploadError(null);
    setSelectedFiles(files);
  };

  const handleUpload = () => {
    if (!userId) {
      setUploadError('User ID not available. Please ensure you are logged in.');
      return;
    }
    
    selectedFiles.forEach(file => {
      const tempRef = `${file.name}_${Date.now()}`;
      setPendingUploads(prev => ({ ...prev, [tempRef]: file }));
      
      competitiveResearchWebSocketService.sendMessage({
        action: 'request_upload_url',
        userId: userId,
        filename: file.name,
        content_type: file.type,
        temp_ref: tempRef
      });
    });
  };

  const handleRemove = (docId) => {
    setUploadedDocs(prev => prev.filter(d => d.doc_id !== docId));
  };

  return (
    <ExpandableSection
      headerText="📎 Attach Files (Optional)"
      variant="footer"
    >
      <SpaceBetween size="m">
        {uploadError && (
          <Alert type="error" dismissible onDismiss={() => setUploadError(null)}>
            {uploadError}
          </Alert>
        )}
        
        <FileUpload
          onChange={handleFileChange}
          value={selectedFiles}
          multiple
          accept=".pdf,.docx,.pptx"
          constraintText="PDF, DOCX, or PPTX. Max 50MB per file, 10 files total."
          i18nStrings={{
            uploadButtonText: e => e ? "Choose files" : "Choose file",
            dropzoneText: e => e ? "Drop files to upload" : "Drop file to upload",
            removeFileAriaLabel: e => `Remove file ${e + 1}`,
            limitShowFewer: "Show fewer files",
            limitShowMore: "Show more files"
          }}
        />
        
        {selectedFiles.length > 0 && (
          <Button variant="primary" onClick={handleUpload}>
            Upload {selectedFiles.length} file(s)
          </Button>
        )}
        
        {Object.keys(uploadProgress).length > 0 && (
          <Box>
            {Object.entries(uploadProgress).map(([docId, progress]) => {
              const file = selectedFiles.find(f => f.tempId === docId);
              return (
                <Box key={docId} margin={{ bottom: 's' }}>
                  <Box variant="small">{file?.name}</Box>
                  <ProgressBar
                    value={progress}
                    variant={progress === -1 ? 'error' : 'standalone'}
                    label={progress === 100 ? 'Complete' : progress === -1 ? 'Failed' : 'Uploading'}
                  />
                </Box>
              );
            })}
          </Box>
        )}
        
        {uploadedDocs.length > 0 && (
          <Container header={<Box variant="h3">Uploaded Files ({uploadedDocs.length})</Box>}>
            <SpaceBetween size="xs">
              {uploadedDocs.map(doc => (
                <Box key={doc.doc_id}>
                  <SpaceBetween direction="horizontal" size="xs">
                    <Box>📄 {doc.filename}</Box>
                    <Button
                      variant="inline-icon"
                      iconName="close"
                      onClick={() => handleRemove(doc.doc_id)}
                    />
                  </SpaceBetween>
                </Box>
              ))}
            </SpaceBetween>
          </Container>
        )}
      </SpaceBetween>
    </ExpandableSection>
  );
}
